import { Children, cloneElement, Fragment, isValidElement, useMemo, useRef, useState } from 'react';

import { styled } from '@mui/material';
import { useTranslation } from '@kc-console/core';
import {
  IcChevronDownSmallRegular,
  IcChevronUpSmallRegular,
  IcMoreVerticalRegular,
} from '@kep/kcds-icons';

import { ButtonGhost } from '../ButtonGhost';
import { Divider } from '../Divider';
import { DropdownMenu2 } from '../DropdownMenu2';
import { IconButton } from '../IconButton';
import { ButtonGroupItem } from './ButtonGroupItem';
import { parseDropdownItems } from './parseDropdownItems';
import { buttonGroupClasses } from './theme';

import type { ReactElement } from 'react';
import type { ButtonProps } from '../Button/types';
import type { ButtonGroupItemData, ButtonGroupOwnerState, ButtonGroupProps } from './types';

const ButtonGroupContainer = (props: ButtonGroupProps) => {
  const { t } = useTranslation();
  const {
    moreButtonLabel,
    moreButtonSize = 'sm',
    usage = 'action',
    children,
    visibleButtonCount = 5,
    ...others
  } = props;

  const ownerState: ButtonGroupOwnerState = props;

  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLDivElement | null>(null);

  const childArray = Children.toArray(children);

  const buttonItems = useMemo(() => {
    return childArray.slice(0, visibleButtonCount).map(child => {
      if (!isValidElement(child)) return child;

      // ButtonGroup.Item인 경우만 처리
      if (child.type === ButtonGroupItem) {
        const {
          onClick,
          disabled,
          children: buttonElement,
          showDividerAbove,
        } = child.props as ButtonGroupItemData;

        // Button에 onClick과 disabled 주입
        if (isValidElement(buttonElement)) {
          const button = cloneElement(buttonElement as ReactElement<ButtonProps>, {
            onClick,
            disabled,
          });

          if (showDividerAbove) {
            return (
              <Fragment key={buttonElement.key ?? child.key}>
                <ButtonGroupDivider orientation="vertical" flexItem thickness={1} margin={4} />
                {button}
              </Fragment>
            );
          }

          return cloneElement(buttonElement as ReactElement<ButtonProps>, {
            key: buttonElement.key || child.key,
            onClick,
            disabled,
          });
        }
      }

      return child;
    });
  }, [childArray, visibleButtonCount]);

  // 드롭다운에 들어갈 아이템들만 파싱
  const dropdownItems = useMemo(() => {
    if (childArray.length <= visibleButtonCount) {
      return [];
    }
    return parseDropdownItems(children, visibleButtonCount);
  }, [children, visibleButtonCount, childArray.length]);

  const showDropdown = dropdownItems.length > 0;

  return (
    <ButtonGroupRoot ownerState={ownerState} {...others}>
      {/* button 렌더링 */}
      {buttonItems}

      {/* 드롭다운 버튼 및 메뉴 */}
      {showDropdown && (
        <div ref={anchor}>
          {usage === 'detail' ? (
            <ButtonGroupDetail
              ownerState={ownerState}
              size={moreButtonSize}
              variant="tertiary"
              aria-label={t('ui:action.show_more')}
              onClick={() => setOpen(prev => !prev)}
            >
              <IcMoreVerticalRegular />
            </ButtonGroupDetail>
          ) : (
            <ButtonGroupDropdown
              ownerState={ownerState}
              size={moreButtonSize}
              variant="primary"
              endIcon={open ? <IcChevronUpSmallRegular /> : <IcChevronDownSmallRegular />}
              onClick={() => setOpen(prev => !prev)}
            >
              {moreButtonLabel ?? t('ui:action.show_more')}
            </ButtonGroupDropdown>
          )}
          <ButtonGroupDropdownMenu
            ownerState={ownerState}
            anchorEl={anchor.current}
            onClose={() => setOpen(false)}
            open={open}
            items={dropdownItems}
          />
        </div>
      )}
    </ButtonGroupRoot>
  );
};

// Compound Component로 export
export const ButtonGroup = Object.assign(ButtonGroupContainer, {
  Item: ButtonGroupItem,
});

const ButtonGroupRoot = styled('div', {
  target: buttonGroupClasses.root,
})<{ ownerState: ButtonGroupOwnerState }>`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ButtonGroupDetail = styled(IconButton, {
  target: buttonGroupClasses.detail,
})<{ ownerState: ButtonGroupOwnerState }>``;

const ButtonGroupDropdown = styled(ButtonGhost, {
  target: buttonGroupClasses.dropdown,
})<{ ownerState: ButtonGroupOwnerState }>`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  white-space: nowrap;
`;

const ButtonGroupDropdownMenu = styled(DropdownMenu2, {
  target: buttonGroupClasses.dropdownMenu,
})<{ ownerState: ButtonGroupOwnerState }>``;

const ButtonGroupDivider = styled(Divider)`
  margin: 4px 0;
`;
